import React, { useMemo, useCallback } from 'react';
import { Offer } from '../types';
import OfferCard from './OfferCard';
import { useTranslation } from '../hooks/useTranslation';

interface OfferCarouselProps {
  offers: Offer[];
  currency?: string;
  language?: string;
  user?: any;
}

const OfferCarousel: React.FC<OfferCarouselProps> = React.memo(({ offers, currency, language, user }) => {
  const { t } = useTranslation(language);
  
  // Find minimum price for best offer detection
  const minPrice = useMemo(() => {
    return offers.reduce((min, offer) => {
      return offer.per_bottle_price !== null && offer.per_bottle_price !== undefined && offer.per_bottle_price < min 
        ? offer.per_bottle_price 
        : min;
    }, Infinity);
  }, [offers]);

  const hasValidPrices = minPrice !== Infinity;

  const handleCardClick = useCallback((buyNowUrl: string) => {
    window.open(buyNowUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleInfoClick = useCallback((e: React.MouseEvent, moreInfoUrl: string) => {
    e.stopPropagation();
    window.open(moreInfoUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleShareClick = useCallback((e: React.MouseEvent, offer: Offer, moreInfoUrl: string) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: `${offer.display_name} ${offer.vintage}`,
        text: t('checkOutWine', { name: offer.display_name, vintage: offer.vintage }),
        url: moreInfoUrl
      }).catch(err => console.error('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(moreInfoUrl)
        .then(() => alert(t('linkCopied')))
        .catch(err => console.error('Error copying link:', err));
    }
  }, [t]);

  return (
    <div className="offer-carousel-container">
      <div className="offer-carousel">
        {offers.map((offer, index) => (
          <OfferCard
            key={`${offer.offer_id}-${index}`}
            offer={offer}
            isBestOffer={hasValidPrices && offer.per_bottle_price === minPrice}
            currency={currency}
            language={language}
            user={user}
            onCardClick={handleCardClick}
            onInfoClick={handleInfoClick}
            onShareClick={handleShareClick}
          />
        ))}
      </div>
    </div>
  );
});

OfferCarousel.displayName = 'OfferCarousel';

export default OfferCarousel;
